import { DownOutlined } from '@ant-design/icons';
import { Badge, Button, Dropdown, message, Tooltip } from 'antd';
import BulkInvitePlayerToSeasonDialog from 'components/BulkInvitePlayerToSeasonDialog';
import Confirm from 'components/Confirm';
import HmsEntityTableController from 'components/HmsEntityTableController';
import PaymentMethodColumn from 'components/PaymentMethodColumn';
import PaymentStatusColumn from 'components/PaymentStatusColumn';
import RegistrationStatusColumn from 'components/RegistrationStatusColumn';
import useEntities from 'hooks/useEntities';
import useIsMobile from 'hooks/useIsMobile';
import useOrganization from 'hooks/useOrganization';
import { useRef, useState } from 'react';
import useLoginStore from 'store/loginStore';
import { patchEntity } from 'utils/apiFunctions';
import icons from 'utils/icons';
import { getPaymentStatusOfSeasonPlayerRegistration } from 'utils/status';
import { useRemovePlayerRegistrationModal } from 'utils/useRemovePlayerRegistrationModal';
import { hasPermission } from 'utils/userUtils';

const SeasonPlayerRegistrationsAdmin = () => {
  const entities = useEntities();
  const entity = entities.SeasonPlayerRegistration;
  const { organization } = useOrganization();
  const organizationId = organization?.organizationId;
  const isMobile = useIsMobile();
  const tableRef = useRef();
  const { showRemovePlayerSeasonRegistrationWithConfirm } = useRemovePlayerRegistrationModal();

  // Check user roles
  const isAdmin = useLoginStore(s => s.isAdmin)();
  const user = useLoginStore(s => s.user);
  const currentRole = useLoginStore(s => s.getCurrentRole)();
  
  // Check if user has permission to edit payment/registration status
  const canEditRegStatuses = hasPermission(user, 'CanEditSeasonPlayerRegistrationPayment') || currentRole === 'SYSTEM_ADMIN';

  // Invite to Season dialog state
  const [inviteToSeasonOpen, setInviteToSeasonOpen] = useState(false);
  const [inviteToSeasonLoading, setInviteToSeasonLoading] = useState(false);
  const [inviteToSeasonResults, setInviteToSeasonResults] = useState(null);

  // Selection state
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  // Handler to open Invite to Season dialog
  const handleOpenInviteToSeason = () => {
    setInviteToSeasonOpen(true);
  };

  // Handler to send invites to season
  const handleSendSeasonInvites = async (selectedPlayers, selectedSeasonIds, advanceStep) => {
    setInviteToSeasonLoading(true);
    setInviteToSeasonResults(null);
    try {
      const playerIds = selectedPlayers.map(p => p.playerId);
      const res = await import('utils/apiFunctions').then(mod => mod.bulkInviteToSeason({ playerIds, seasonIds: selectedSeasonIds }, currentRole));
      const json = await res.json();
      setInviteToSeasonResults(
        (json.results || null)
          ? json.results.map(r => {
            const player = (selectedPlayers || []).find(p => p.playerId === r.playerId);
            return {
              ...r,
              firstName: player?.firstName || '',
              lastName: player?.lastName || '',
              seasonName: r.seasonName || r.seasonId,
            };
          })
          : null
      );
      if (advanceStep) advanceStep();
      if (json.status === 'OK') {
        message.success(json.message || 'Invitations were sent successfully.');
        // Refresh the table after successful invitation
        tableRef.current?.reload();
      } else if (json.status === 'PARTIAL_ERROR') {
        message.warning(json.message || 'Some invitations failed. Please review the results.');
      } else if (json.status === 'ERROR') {
        message.error(json.message || 'All invitations failed.');
      } else {
        message.error(json.message || 'Error sending invitations.');
      }
    } catch (e) {
      message.error('Error communicating with the server.');
    } finally {
      setInviteToSeasonLoading(false);
    }
  };

  // Handler to close invite to season dialog with refresh
  const handleCloseInviteToSeason = () => {
    setInviteToSeasonOpen(false);
    setInviteToSeasonResults(null);
  };

  // Handler for selection changes
  const handleSelectionChange = (selectedRowKeys, selectedRows) => {
    console.log('Final selection:', selectedRowKeys.length, 'items selected');
    setSelectedRowKeys(selectedRowKeys);
    setSelectedRows(selectedRows);
  };


  // Helper function to create confirmation dialog
  const showBulkActionConfirmation = (actionName, onConfirm) => {
    let message = (
      <div>
        <div>Are you sure to perform the following operation on all selected records?</div>
        <div className="grid grid-cols-[auto_1fr] gap-x-2 mt-2">
          <div>Operation:</div><div><b>{actionName}</b></div>
          <div>Number of records:</div><div><b>{selectedRowKeys.length}</b></div>
        </div>
      </div>
    );
    
    setConfirmDialog({
      open: true,
      title: `Confirm bulk change`,
      message: (
        <div>
          <div>{message}</div>
        </div>
      ),
      onConfirm,
    });
  };

  // Handler to close confirmation dialog
  const handleCloseConfirmation = () => {
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      onConfirm: null,
    });
  };

  /////// Bulk action handlers

  const handleBulkMarkAsPaidInCash = () => {
    showBulkActionConfirmation('Mark as Paid in Cash', async () => {
      const registrationsToUpdate = selectedRows.filter(record => {
        const paymentStatus = getPaymentStatusOfSeasonPlayerRegistration(record);
        return paymentStatus !== 'FREE';
      });

      await bulkUpdate(registrationsToUpdate, {
        isPaid: true,
        paymentMethod: 'CASH',
        isConfirmed: true,
      });
    });
  };

  const handleBulkMarkAsUnpaid = () => {
    showBulkActionConfirmation('Mark as Unpaid', async () => {
      const registrationsToUpdate = selectedRows.filter(record => {
        const paymentStatus = getPaymentStatusOfSeasonPlayerRegistration(record);
        return paymentStatus !== 'FREE';
      });

      await bulkUpdate(registrationsToUpdate, {
        isPaid: false,
        isConfirmed: false,
      });

    });
  };

  const handleBulkMarkAsConfirmed = (isConfirmed) => {
    showBulkActionConfirmation(isConfirmed ? 'Mark as Confirmed' : 'Mark as Not Confirmed', async () => {
      const registrationsToUpdate = selectedRows.filter(record => {
        return record.isConfirmed !== isConfirmed;
      })

      await bulkUpdate(registrationsToUpdate, {
        isConfirmed
      });

    });
  };

  const bulkUpdate = async (registrationsToUpdate, fieldsToPatch) => {
    if (registrationsToUpdate.length === 0) {
      message.info('No registrations to update (all selected registrations are FREE)');
      tableRef.current?.reload();
      return;
    }

    try {
      const response = await entity.bulkUpdate(
        registrationsToUpdate.map(record => ({
          [entity.primaryKey]: record[entity.primaryKey],
          ...fieldsToPatch,
        }))
      );
      // const promises = registrationsToUpdate.map(record =>
      //   patchEntity(organizationId, entity, record.seasonPlayerRegistrationId, fieldsToPatch)
      // );
      // await Promise.all(promises);

      if (response.status != 200) {
        throw new Error(`Status ${response.status}`);
      }

      let successMessage = `${registrationsToUpdate.length} record(s) updated`;

      message.success(successMessage);
      tableRef.current?.reload();
      tableRef.current?.clearSelection();
    } catch (error) {
      message.error(`Failed to update ${selectedRowKeys.length} record(s). Please retry.`);
      console.error('Error updating records:', error);
    }
  }

  /////// Single action handlers

  const handlePaymentStatusChange = async (record, newStatus) => {
    await singleRecordUpdate(record, { isPaid: newStatus == 'PAID' });
  };

  // Handler to update payment method
  const handlePaymentMethodChange = async (record, newMethod) => {
    await singleRecordUpdate(record, { paymentMethod: newMethod });
  };

  // Handler to update registration status
  const handleRegistrationStatusChange = async (record, newStatus) => {
    await singleRecordUpdate(record, { isConfirmed: newStatus === 'CONFIRMED' });
  };

  const singleRecordUpdate = async (record, fieldsToPatch) => {
    try {
      await patchEntity(organizationId, entity, record.seasonPlayerRegistrationId, fieldsToPatch, currentRole);
      message.success(`Record updated`);
      // Refresh the table after successful update
      tableRef.current?.reload();
    } catch (error) {
      message.error('Failed to update record');
      console.error('Error updating record:', error);
    }
  };

  ///////

  function renderActionColumn(record) {
    const season = record.Season;
    if (!season) return null;

    return (
      <span className="flex flex-nowrap gap-4 pointer-events-none">
        <button className="flex items-center gap-1 pointer-events-auto hover:text-red-500 bg-transparent border-none cursor-pointer"
          onClick={(e) => { e.stopPropagation(); handleRemoveRegistration(record); }}
        >
          <span className="flex items-center gap-1">
            {icons.Trash}
            Remove
          </span>
        </button>
      </span>
    );
  }

  const filterGroups = null;
  const showColumns = [
    {
      column: 'CreatedAt',
      defaultSortOrder: 'descend',
      //visible: false,
    },
    'Player.NameWithLogo',
    'Player.TeamsShort',
    'Competition.Name',
    'Season.Name',
    // Conditional columns based on user role
    'Season.ParticipationFee',
    'AcceptedByPlayer',
    ...(isAdmin ? [{
      column: 'PaymentStatus',
      width: 150,
      render: (record) => (
        <PaymentStatusColumn
          record={record}
          isAdmin={isAdmin}
          canEdit={canEditRegStatuses}
          onPaymentStatusChange={handlePaymentStatusChange}
        />
      ),
    }] : ['PaymentStatus']),
    ...(isAdmin ? [{
      column: 'PaymentMethod',
      width: 150,
      render: (record) => (
        <PaymentMethodColumn
          record={record}
          isAdmin={isAdmin}
          canEdit={canEditRegStatuses}
          onPaymentMethodChange={handlePaymentMethodChange}
        />
      ),
    }] : ['PaymentMethod']),
    ...(isAdmin ? [{
      column: 'RegistrationStatus',
      width: 190,
      render: (record) => (
        <RegistrationStatusColumn
          record={record}
          isAdmin={isAdmin}
          canEdit={canEditRegStatuses}
          onRegistrationStatusChange={handleRegistrationStatusChange}
        />
      ),
    }] : ['RegistrationStatus']),
    ...(isAdmin ? [
      'UpdatedAt',
      'CreatedAt',
    ] : []),
    // Action column only for admins
    ...(isAdmin ? [{
      title: 'Action',
      key: 'action',
      width: 100,
      render: (record) => renderActionColumn(record),
    }] : []),
    // Export-only column: Link to Player detail (hidden from display, available for export)
    {
      title: 'Player Link',
      render: (record) => {
        if (record.Player?.playerId) {
          return window.location.protocol + '//' + window.location.host + entities.Player.entityUrl(record.Player);
        }
        return '';
      },
      visible: false,
      exported: true,
      filterDisabled: true,
      requiredRelations: ['Player(playerId)'],
    },
  ];

  const renderMobileRow = (record) => {
    return (
      <div className="p-3 border rounded bg-white shadow-sm flex flex-col gap-3 cursor-default">
        <span>{entity.columns['Player.NameWithLogo'].render(record)}</span>
        <div className="text-sm text-gray-600 flex flex-wrap gap-2 items-center">
          <span>{entity.columns['Player.TeamsShort'].render(record)}</span>
        </div>
        <div className="text-sm text-gray-600 flex flex-wrap gap-2 items-center">
          <span>{entity.columns['Competition.Name'].render(record)}</span>
          <span>•</span>
          <span>{entity.columns['Season.Name'].render(record)}</span>
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-2 gap-x-4">
          <span>Accepted by Player:</span>
          <span>{entity.columns['Player_AcceptedByPlayer'].render(record)}</span>
          <span>Paid:</span>
          <span>{entity.columns['Player_Paid'].render(record)}</span>
          <span>Confirmed:</span>
          <span>{entity.columns['Player_Confirmed'].render(record)}</span>
        </div>
        {isAdmin && renderActionColumn(record)}
      </div>
    );
  };

  const handleRemoveRegistration = (record) => {
    if (!isAdmin) return;
    
    showRemovePlayerSeasonRegistrationWithConfirm(record, () => {
      tableRef.current?.reload();
    }, currentRole);
  }

  // Bulk actions dropdown menu
  const bulkActionsMenu = {
    items: [
      {
        key: 'markPaid',
        label: 'Mark as Paid in Cash',
        onClick: handleBulkMarkAsPaidInCash,
        disabled: selectedRowKeys.length === 0,
      },
      {
        key: 'markUnpaid',
        label: 'Mark as Unpaid',
        onClick: handleBulkMarkAsUnpaid,
        disabled: selectedRowKeys.length === 0,
      },
      {
        type: 'divider',
      },
      {
        key: 'markConfirmed',
        label: 'Mark as Confirmed',
        onClick: () => handleBulkMarkAsConfirmed(true),
        disabled: selectedRowKeys.length === 0,
      },
      {
        key: 'marNotConfirmed',
        label: 'Mark as Not Confirmed',
        onClick: () => handleBulkMarkAsConfirmed(false),
        disabled: selectedRowKeys.length === 0,
      },
      {
        type: 'divider',
      },
      {
        key: 'selectAll',
        label: 'Select All',
        onClick: () => tableRef.current?.selectAll(),
      },
      {
        key: 'unselectAll',
        label: 'Deselect All',
        onClick: () => tableRef.current?.unselectAll(),
        disabled: selectedRowKeys.length === 0,
      },
    ],
  };

  if (!organization?.seasonPlayerRegistrationEnabled) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold mb-4">Player Season Registrations</h2>
        <p className="text-gray-600">
          Player season registration is currently disabled for this organization.
        </p>
      </div>
    );
  }

  return (
    <>
      <HmsEntityTableController
        ref={tableRef}
        entity={entity}
        // HMS-75: load only the fields listed below (without it, the backend loaded all columns of Player/Season incl. Season terms => ~95 MB response on prod)
        // When adding a column / feature to this page, the fields it reads must be added here.
        leanRelations={true}
        relations={[
          'Player(firstName,lastName,logoUrl,playerId)',
          'Player>Teams(name,logoUrl,teamId)',
          'Season(name,seasonId,playerRegFeeMandatory,playerRegFee)',
          'Season>Competition(name,logoUrl,competitionId)',
        ]}
        onlyAttributes={[
          'seasonPlayerRegistrationId',
          'createdAt',
          'updatedAt',
          'updatedBy',
          'isAccepted',
          'isPaid',
          'isConfirmed',
          'paymentMethod',
        ]}
        filterGroups={filterGroups}
        skipAutoLinkColumn={true}
        extraButtonsLeft={
          <>
            {organization?.seasonPlayerRegistrationEnabled && (
              <Button
                type="primary"
                size={isMobile ? 'small' : 'small'}
                icon={icons.Player}
                onClick={() => handleOpenInviteToSeason()}
              >
                Invite Players
              </Button>
            )}
            {isAdmin && canEditRegStatuses && (
              <Badge
                count={selectedRowKeys.length}
                overflowCount={999}
              >
                <Dropdown
                  menu={bulkActionsMenu}
                  trigger={['click']}>
                  <Button
                    size={isMobile ? 'small' : 'small'}
                  >
                    Bulk Actions
                    <DownOutlined />
                  </Button>
                </Dropdown>
              </Badge>
            )}
          </>
        }
        extraButtonBarItems={({ filteredData }) => (
          <>
            <div className="flex items-center gap-2">
              <Tooltip className="cursor-pointer" overlayStyle={{ maxWidth: '450px' }} title={
                <div>
                  <div>
                    In this view you can find all player registrations for seasons.<br /><br />
                    <span className="underline">Invite Players:</span> Send invitations to Players to register for a Season.
                    {isAdmin && (
                      <>
                        <br /><br />
                        <span className="underline">Admin Features:</span> You can also update payment status, payment method, registration status, remove registrations, and perform bulk actions on selected registrations.
                      </>
                    )}
                  </div>
                </div>
              }>
                {icons.Information}
              </Tooltip>
            </div>
          </>
        )}
        columnSets={[
          { name: 'All', columns: showColumns },
          { name: 'Waiting For Accepting', filters: { isAccepted: false }, columns: showColumns },
          { name: 'Waiting For Payment', filters: { isAccepted: true, isPaid: false, isConfirmed: false }, columns: showColumns },
          { name: 'Confirmed', filters: { isConfirmed: true }, columns: showColumns },
        ]}
        canCreate={false}
        onRowClickLink={() => undefined}
        // Disable certain features for team managers
        exportEnabled={isAdmin}
        columnSelectorEnabled={isAdmin}
        filterEnabled={isAdmin}
        renderMobileRow={renderMobileRow}
        // Enable selection for admins with edit permissions
        selectionEnabled={isAdmin && canEditRegStatuses}
        onSelectionChange={handleSelectionChange}
        rowKey={(record) => record.seasonPlayerRegistrationId || record.key || record.id}
        showSelectAllButtons={false}
      />

      <BulkInvitePlayerToSeasonDialog
        open={inviteToSeasonOpen}
        onClose={handleCloseInviteToSeason}
        loading={inviteToSeasonLoading}
        onSendInvites={handleSendSeasonInvites}
        inviteResults={inviteToSeasonResults}
      />

      <Confirm
        visible={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmButtonText="Confirm"
        confirmButtonType="primary"
        confirmButtonSize="small"
        cancelButtonText="Cancel"
        cancelButtonSize="small"
        handleConfirm={async () => {
          if (confirmDialog.onConfirm) {
            await confirmDialog.onConfirm();
          }
          handleCloseConfirmation();
        }}
        handleCancel={handleCloseConfirmation}
      />
    </>
  );
}

export default SeasonPlayerRegistrationsAdmin; 